<script setup>
import { ref, computed, watch } from 'vue'
import { useTranslaterStore } from '@/stores/translater'
import { useUsersGroupsStore } from '@/stores/users-groups'

import Modal from '@/components/UTAModal.vue'
import UTAUsersGroupCreate from '@/components/groupsUsers/UTAUsersGroupCreate.vue'

const translaterStore = useTranslaterStore()
const usersGroupsStore = useUsersGroupsStore()

const getTranslate              = computed(() => translaterStore.getTranslate)
const usersGroups                = computed(() => usersGroupsStore.usersGroups)
const usersGroupsProcess         = computed(() => usersGroupsStore.usersGroupsProcess)
const usersGroupsMsg             = computed(() => usersGroupsStore.usersGroupsMsg)
const usersGroupsCreateMsg       = computed(() => usersGroupsStore.usersGroupsCreateMsg)
const usersGroupsDeleteProcess   = computed(() => usersGroupsStore.usersGroupsDeleteProcess)
const usersGroupsDeleteMsg       = computed(() => usersGroupsStore.usersGroupsDeleteMsg)

const viewMode              = ref(localStorage.getItem('UTA_GROUPS_VIEW') || 'lines') 
const pageNumber            = ref(1) 
const inputSearch           = ref('') 
const deleteGroupUUID       = ref(null) 
const showCreateUsersGroup   = ref(false) 
const showDeleteUsersGroup   = ref(false)

function prevPage(cursor) {

    let params = {
        'c.limit': 12, 
        'c.direction': 'before', 
        'c.cursor': cursor
    }

    if(inputSearch.value) 
        params['c.q'] = inputSearch.value

    usersGroupsStore.readUsersGroups(params).then(() => {
        pageNumber.value--
    })
}

function nextPage(cursor) {

    let params = {
        'c.limit': 12, 
        'c.direction': 'after', 
        'c.cursor': cursor
    }

    if(inputSearch.value) 
        params['c.q'] = inputSearch.value

    usersGroupsStore.readUsersGroups(params).then(() => {
        pageNumber.value++
    })
}

function setViewMode(mode) {

    viewMode.value = mode
    localStorage.setItem('UTA_GROUPS_VIEW', mode)
}

function submitDeleteUsersGroups() {

    usersGroupsStore.deleteUsersGroups(deleteGroupUUID.value)
}

usersGroupsStore.readUsersGroups({ 'c.limit': 12 })

watch(inputSearch, input => {

    input.length > 2 ? 
        usersGroupsStore.readUsersGroups({ 'c.limit': 12, 'c.q': inputSearch.value }) :
        usersGroupsStore.readUsersGroups({ 'c.limit': 12 })
})

watch(usersGroupsCreateMsg, msg => {

    if('success' in msg && msg.success) {

        usersGroupsStore.readUsersGroups({ 'c.limit': 12 })
        showCreateUsersGroup.value = false
    }
})

watch(usersGroupsDeleteMsg, msg => {

    if('success' in msg && msg.success) {

        usersGroupsStore.readUsersGroups({ 'c.limit': 12 })
        showDeleteUsersGroup.value = false
    }
})
</script>

<template>

    <div class="my-5 uta-users-groups">

        <div class="container-fluid">

            <header class="page__header uta-header" role="heading" aria-level="1">
                <h2 class="uta-header__title">{{ getTranslate.USERS_GROUPS }}</h2>
                <div class="spinner-border uta-header__spinner" role="status" v-if="usersGroupsProcess && usersGroups.array?.length">
                    <span class="visually-hidden">Loading...</span>
                </div>
                <div class="uta-header__actions">
                    <a 
                        href="#" 
                        class="uta-btn uta-btn--main uta-btn--sm uta-btn--has-icon uta-header__action uta-header__action--create"
                        @click.prevent="showCreateUsersGroup = true"
                    >
                        <span class="uta-btn__content">
                            <span class="uta-btn__icon material-icons">add</span>
                            <span class="uta-btn__text uta-btn__text--has-icon d-none d-md-block">{{ getTranslate.BTN_ADD }}</span>
                        </span>
                    </a>
                </div>
            </header>
        
            <div class="page__content">
                <div class="row mb-4 justify-content-between">
                    <div class="col-12 col-md-8 col-lg-10">
                        <div class="uta-input__wrapper uta-search">
                            <input 
                                type="text" 
                                :placeholder="getTranslate.SEARCH + '...'" 
                                v-model="inputSearch"
                                @keyup.escape="inputSearch = ''"
                                class="form-control uta-input__wrapper-input uta-search__input" 
                            >
                            <template v-if="inputSearch.length > 2">
                                <span class="material-icons uta-input__wrapper-icon" @click="inputSearch = ''">
                                    cancel
                                </span>
                            </template>
                            <template v-else>
                                <span class="material-icons uta-input__wrapper-icon">
                                    search
                                </span>
                            </template>
                        </div>
                    </div>
                    <div class="d-none d-md-flex view-selector">
                        <a href="#" class="view-selector__item view-selector__item--lines" @click.prevent="setViewMode('lines')"></a>
                        <a href="#" class="view-selector__item view-selector__item--grid" @click.prevent="setViewMode('grid')"></a>
                    </div>
                </div>
                <div class="loader loader--view-lg" v-if="usersGroupsProcess && !usersGroups.array?.length">
                    <div class="spinner-border text-secondary" role="status">
                        <span class="visually-hidden">Loading...</span>
                    </div>
                </div>
                <div class="my-4 uta-alert uta-alert--success" v-if="'success' in usersGroupsCreateMsg && usersGroupsCreateMsg.success">
                    <span class="material-icons uta-alert__icon">check_circle_outline</span>
                    <div class="uta-alert__text">
                        {{ getTranslate[usersGroupsCreateMsg.text] || usersGroupsCreateMsg.text }}
                    </div>
                    <button type="button" class="uta-alert__close" @click="usersGroupsStore.setUsersGroupsCreateMsg({})">
                        <span class="material-icons">close</span>
                    </button>
                </div>
                <div class="my-4 uta-alert uta-alert--success" v-if="'success' in usersGroupsDeleteMsg && usersGroupsDeleteMsg.success">
                    <span class="material-icons uta-alert__icon">check_circle_outline</span>
                    <div class="uta-alert__text">
                        {{ getTranslate[usersGroupsDeleteMsg.text] || usersGroupsDeleteMsg.text }}
                    </div>
                    <button type="button" class="uta-alert__close" @click="usersGroupsStore.setUsersGroupsDeleteMsg({})">
                        <span class="material-icons">close</span>
                    </button>
                </div>
                <div 
                    class="my-4 uta-alert uta-alert--danger uta-group-users__message" 
                    v-if="'success' in usersGroupsMsg && !usersGroupsMsg.success"
                >
                    <span class="material-icons uta-alert__icon">error_outline</span>
                    <div class="uta-alert__text">
                        {{ getTranslate[usersGroupsMsg.text] || usersGroupsMsg.text }}
                    </div>
                </div>
                <template v-if="usersGroups.array?.length">
                    <template v-if="viewMode == 'lines'">
                        <div class="container-fluid">
                            <div class="uta-users-groups__list uta-list">
                                <div class="uta-list__title d-none d-lg-block uta-users-groups__title">
                                    <div class="row">
                                        <div class="col-10">
                                            <div class="row">
                                                <div class="col-6">{{ getTranslate.USERS_GROUPS_NAME }}</div>
                                                <div class="col-6">{{ getTranslate.USERS_GROUPS_DESCR }}</div>
                                            </div>
                                        </div>
                                        <div class="col-2"></div>
                                    </div>
                                </div>
                                <div class="pb-4 uta-list__content">
                                    <div 
                                        class="row align-items-center uta-list__item uta-users-groups__group" 
                                        :class="{ 'uta-list__item--dark' : index % 2 }" 
                                        v-for="(group, index) in usersGroups.array" 
                                        :key="group.vin"
                                    >
                                        <div class="col-10">
                                            <div class="row">
                                                <div class="col-12 col-lg-6 uta-users-groups__group-name">
                                                    <span class="d-inline d-lg-none uta-users-groups__group-mobile-title">{{ getTranslate.USERS_GROUPS_NAME }}: </span>
                                                    <router-link :to="'/users-groups/' + group.uuid" class="uta-list__item-link uta-users-groups__link">{{ group.name }}</router-link>
                                                </div>
                                                <div class="col-12 col-lg-6 uta-users-groups__group-descr">
                                                    <span class="d-inline d-lg-none uta-users-groups__group-mobile-title">{{ getTranslate.USERS_GROUPS_DESCR }}: </span>
                                                    {{ group.description }}
                                                </div>
                                            </div>
                                        </div>
                                        <div class="col-2">
                                            <div class="text-end uta-users-groups__group-delete">
                                                <span 
                                                    class="material-icons uta-list__item-icon uta-users-groups__delete-icon"
                                                    @click="
                                                        showDeleteUsersGroup = true, 
                                                        deleteGroupUUID = group.uuid
                                                    "
                                                >
                                                    delete
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </template>
                    <template v-if="viewMode == 'grid'">
                        <div class="row">
                            <div 
                                class="mb-4 col-24 col-sm-12 col-md-8 col-lg-6 col-xxl-4 uta-users-groups__group grid" 
                                v-for="group in usersGroups.array" 
                                :key="group.uuid"
                            >
                                <div class="grid__item">
                                    <span 
                                        class="material-icons grid__item-icon" 
                                        @click="
                                            showDeleteUsersGroup = true, 
                                            deleteGroupUUID = group.uuid
                                        "
                                    >
                                        delete
                                    </span>
                                    <div class="grid__item-title">
                                        {{ group.name }}
                                    </div>
                                    <div class="grid__item-subtitle">
                                        {{ group.description || '-' }}
                                    </div>
                                    <router-link :to="'/users-groups/' + group.uuid" class="grid__item-readmore">
                                        {{ getTranslate.BTN_READMORE }}
                                    </router-link>
                                </div>
                            </div>
                        </div>
                    </template>
                    <div class="row" v-if="usersGroups.c.isBefore || usersGroups.c.isAfter">
                        <div class="my-4 uta-pagination col-24 col-lg-6 col-xxl-4">
                            <div class="uta-pagination__prev" :class="{ 'uta-pagination__prev--active' : usersGroups.c.isBefore }">
                                <template v-if="usersGroups.c.isBefore">
                                    <button 
                                        type="button"
                                        class="uta-pagination__prev-link" 
                                        :disabled="usersGroupsProcess"
                                        @click.prevent="prevPage(usersGroups.c.start)"
                                    >
                                        <div class="uta-pagination__prev-link-spinner spinner-border text-secondary" role="status" v-if="usersGroupsProcess">
                                            <span class="visually-hidden">Loading...</span>
                                        </div>
                                        <span class="material-icons" v-else>
                                            chevron_left
                                        </span>
                                    </button>
                                </template>
                                <template v-else>
                                    <span class="material-icons">
                                        chevron_left
                                    </span>
                                </template>
                            </div>
                            <div class="uta-pagination__page">
                                {{ pageNumber }}
                            </div>
                            <div class="uta-pagination__next" :class="{ 'uta-pagination__next--active' : usersGroups.c.isAfter }">
                                <template v-if="usersGroups.c.isAfter">
                                    <button 
                                        type="button"
                                        class="uta-pagination__next-link" 
                                        :disabled="usersGroupsProcess"
                                        @click.prevent="nextPage(usersGroups.c.stop)"
                                    >
                                        <div class="uta-pagination__next-link-spinner spinner-border text-secondary" role="status" v-if="usersGroupsProcess">
                                            <span class="visually-hidden">Loading...</span>
                                        </div>
                                        <span class="material-icons" v-else>
                                            chevron_right
                                        </span>
                                    </button>
                                </template>
                                <template v-else>
                                    <span class="material-icons">
                                        chevron_right
                                    </span>
                                </template>
                            </div>
                        </div>
                    </div>
                </template>
            </div>
        </div>
        <transition name="bounce">
            <Modal 
                v-if="showCreateUsersGroup"
                :header="getTranslate.USERS_GROUPS_CREATE"
                size="md"
                @closeModal="showCreateUsersGroup = false, usersGroupsStore.setUsersGroupsCreateMsg({})"
            >
                <template v-slot:body>
                    <UTAUsersGroupCreate @closeModal="showCreateUsersGroup = false, usersGroupsStore.setUsersGroupsCreateMsg({})" />
                </template>
            </Modal>
        </transition>
        <transition name="bounce">
            <Modal 
                v-if="showDeleteUsersGroup"
                :header="getTranslate.USERS_GROUPS_DELETE"
                size="md"
                @closeModal="showDeleteUsersGroup = false"
            >
                <template v-slot:body>
                    <form action="" @submit.prevent="submitDeleteUsersGroups">
                        {{ getTranslate.USERS_GROUPS_DELETE_DESCR }}
                        <div class="mt-5 uta-btns">
                            <button type="button" class="uta-btn" @click="showDeleteUsersGroup = false">{{ getTranslate.BTN_CANCEL }}</button>
                            <button type="submit" class="uta-btn uta-btn--main" :disabled="usersGroupsDeleteProcess">{{ getTranslate.BTN_DELETE }}</button>
                        </div>
                    </form>
                </template>
            </Modal>
        </transition>
    </div>
</template>

<style lang="scss">
@import '@/assets/styles/scss/components/users-groups.scss';
</style>